import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail } from 'lucide-react';
import { FaInstagram, FaYoutube, FaTelegramPlane, FaWhatsapp } from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdowns, setMobileDropdowns] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when route changes
  useEffect(() => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
    setMobileDropdowns({});
  }, [location.pathname]);

  // Close dropdown when clicking outside or pressing Escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  // Handle course navigation
  const handleCourseNavigation = (courseId) => {
    if (location.pathname === '/courses') {
      const element = document.querySelector(`#${courseId}`);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    } else {
      navigate(`/courses#${courseId}`);
    }
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  // Handle about us navigation
  const handleAboutUsNavigation = (sectionId) => {
    if (location.pathname === '/about-us') {
      const element = document.querySelector(`#${sectionId}`);
      if (element) {
        const navbarHeight = 140;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      navigate(`/about-us#${sectionId}`);
    }
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  // Handle Downloads navigation
  const handleDownloadNavigation = (category = null) => {
    if (category) {
      window.location.href = `/downloads?category=${category}`;
    } else {
      window.location.href = '/downloads';
    }
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: '/', label: 'Home' },
    {
      id: '/courses',
      label: 'Courses',
      dropdown: [
        { label: 'UPSC', action: () => handleCourseNavigation('upsc') },
        { label: 'UPSC Foundation', action: () => handleCourseNavigation('upsc-foundation') },
        { label: 'UPSC Integrated Batch', action: () => handleCourseNavigation('upsc-integrated') },
        { label: 'Personalized Mentorship', action: () => handleCourseNavigation('personalized-mentorship') },
        { label: 'UPSC Optional Subjects', action: () => handleCourseNavigation('optional-subjects') },
        { label: 'UPSC Essay Writing', action: () => handleCourseNavigation('essay-writing') },
        { label: 'UPSC Ethics Course', action: () => handleCourseNavigation('ethics-plus') },
        { label: 'UPSC 5-Months Answer Writing', action: () => handleCourseNavigation('answer-writing') },
        { label: 'MPSC Optional Subjects', action: () => handleCourseNavigation('mpsc-optional-subjects') },
        { label: 'MPSC', action: () => handleCourseNavigation('mpsc') },
        { label: 'MPSC Foundation Course', action: () => handleCourseNavigation('mpsc-foundation') },
        { label: 'MPSC Integrated Batch', action: () => handleCourseNavigation('mpsc-integrated') },
        { label: 'MPSC वैयक्तिक मार्गदर्शन कार्यक्रम ', action: () => handleCourseNavigation('mpsc-personalized-mentorship') },
        { label: 'MPSC 5-Month Answer Writing', action: () => handleCourseNavigation('mpsc-answer-writing') },
        { label: 'निबंध लेखन प्रशिक्षण कार्यक्रम ', action: () => handleCourseNavigation('mpsc-essay') },
        { label: 'ETHICS कोर्स - MPSC साठी विशेष', action: () => handleCourseNavigation('ethics-mentorship') },
        { label: 'CAPF Mentorship Programme', action: () => handleCourseNavigation('capf-mentorship') },
      ]
    },
    {
      id: '/about-us',
      label: 'About Us',
      dropdown: [
        { label: 'Director Desk', action: () => navigate('/directordesk') },
        { label: 'Meet Our Team', action: () => handleAboutUsNavigation('team') },
        { label: 'Faculty', action: () => handleAboutUsNavigation('faculty') },
        { label: 'Our Mission and Vision', action: () => handleAboutUsNavigation('mission-vision') },
        { label: 'Our Core Values', action: () => handleAboutUsNavigation('core-values') },
        { label: 'Brochure', action: () => window.open('https://dimensionias.com/files/Brochure/BROCHURE%202025-26.pdf') }
      ]
    },
    { id: '/gallery', label: 'Gallery' },
    { id: '/contact', label: 'Contact' },
    {
      id: '/downloads',
      label: 'Download',
      dropdown: [
        { label: 'UPSC Material', action: () => handleDownloadNavigation('upsc') },
        { label: 'MPSC Material', action: () => handleDownloadNavigation('mpsc') },
        { label: 'CAPF Material', action: () => handleDownloadNavigation('capf') },
        { label: 'Books', action: () => handleDownloadNavigation('books') },
      ],
    },
    { id: '/blog', label: 'Blogs' },
    { id: '/currentaffairs', label: 'Current Affairs' }
  ];

  const handleDropdownToggle = (itemId) => {
    setActiveDropdown(activeDropdown === itemId ? null : itemId);
  };

  const handleMobileDropdownToggle = (itemId) => {
    setMobileDropdowns(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-200 bg-white border-b border-[#E0F2F1] ${isScrolled ? 'shadow-md' : 'shadow-xs'
      }`}>
      {/* Top Contact Bar */}
      <div className="w-full bg-[#134E4A] text-xs text-[#F0FDF7]">
        {/* Desktop Contact Bar */}
        <div className="hidden md:block px-2 py-1">
          <div className="max-w-7xl mx-auto flex justify-end items-center gap-5">
            {/* Phone */}
            <div className="flex items-center gap-1">
              <Phone size={12} className="text-[#F0FDF7]" />
              <span className="font-bold">Phone:</span>{" "}
              <a href="tel:+917530807530" className="hover:text-[#2DD4BF] transition-colors">+91 7530807530</a>
            </div>

            {/* Email */}
            <div className="flex items-center gap-1.5">
              <a
                href="https://mail.google.com/mail/?view=cm&fs=1&to=contact@dimensionias.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 hover:text-[#2DD4BF] transition-colors"
              >
                <Mail size={12} className="text-[#F0FDF7]" />
                <span className="font-bold">Email:</span>
                contact@dimensionias.com
              </a>
            </div>
          </div>
        </div>

        {/* Mobile Contact Bar */}
        <div className="md:hidden px-2 py-0.5">
          <div className="flex justify-center items-center gap-3 text-[11px]">
            <a
              href="tel:+917530807530"
              className="flex items-center gap-1 hover:text-[#2DD4BF] transition-colors font-medium"
            >
              <Phone size={11} className="text-[#F0FDF7]" />
              <span>+91 7530807530</span>
            </a>
            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=contact@dimensionias.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 hover:text-[#2DD4BF] transition-colors font-medium"
            >
              <Mail size={11} className="text-[#F0FDF7]" />
              <span>contact@dimensionias.com</span>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" ref={dropdownRef}>
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center flex-shrink-0 h-full py-1"
          >
            <img
              src="/logo.png"
              alt="DimensionIAS Logo"
              className="h-10 sm:h-12 w-auto object-contain max-w-[280px] sm:max-w-[320px] transition-transform duration-200 hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1 lg:space-x-2 xl:space-x-3">
            {navItems.map((item) => (
              <div key={item.id} className="relative group">
                {item.dropdown ? (
                  <div className="relative">
                    <button
                      onClick={() => handleDropdownToggle(item.id)}
                      className={`flex items-center gap-1 px-2 py-1 text-xs xl:text-sm font-extrabold transition-colors duration-200 ${location.pathname.startsWith(item.id) ? 'text-[#134E4A]' : 'text-[#134E4A] hover:text-[#2DD4BF]'
                        }`}
                    >
                      {item.label}
                      <ChevronDown
                        size={13}
                        className={`transition-transform duration-200 ${activeDropdown === item.id ? 'rotate-180' : ''
                          }`}
                      />
                    </button>

                    {/* Dropdown Menu */}
                    {activeDropdown === item.id && (
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 w-[600px] bg-white rounded-xl shadow-2xl border border-[#E0F2F1] py-4 px-6 z-[9999] max-h-[80vh] overflow-y-auto">
                        <div className="grid grid-cols-3 gap-2">
                          {item.dropdown.map((dropdownItem, index) => (
                            <div key={index} className="min-w-0">
                              {dropdownItem.action ? (
                                <button
                                  onClick={dropdownItem.action}
                                  className="block w-full text-left text-xs font-bold text-[#134E4A] hover:bg-[#F0FDF7] hover:text-[#2DD4BF] transition-colors duration-150 py-1.5 px-2.5 rounded-md"
                                  title={dropdownItem.label}
                                >
                                  {dropdownItem.label}
                                </button>
                              ) : (
                                <Link
                                  to={dropdownItem.path}
                                  onClick={() => setActiveDropdown(null)}
                                  className="block text-xs font-bold text-[#134E4A] hover:bg-[#F0FDF7] hover:text-[#2DD4BF] transition-colors duration-150 py-1.5 px-2.5 rounded-md"
                                  title={dropdownItem.label}
                                >
                                  {dropdownItem.label}
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.id}
                    onClick={() => setActiveDropdown(null)}
                    className={`px-2 py-1 text-xs xl:text-sm font-extrabold transition-colors duration-200 relative group ${location.pathname === item.id ? 'text-[#134E4A]' : 'text-[#134E4A] hover:text-[#2DD4BF]'
                      }`}
                  >
                    {item.label}
                    <span className={`absolute bottom-0 left-0 h-0.5 bg-[#2DD4BF] transition-all duration-300 ${location.pathname === item.id ? 'w-full' : 'w-0 group-hover:w-full'
                      }`}></span>
                  </Link>
                )}
              </div>
            ))}

            {/* Desktop Social Icons & Enquire Now Button */}
            <div className="flex items-center space-x-2.5 ml-1 border-l border-[#E0F2F1] pl-2.5">
              <div className="flex items-center space-x-1.5">
                <a href="https://www.instagram.com/dimensionias_pune" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                  <FaInstagram className="text-[#134E4A] hover:text-pink-500 transition-colors" size={15} />
                </a>
                <a href="https://www.youtube.com/@DimensionIAS" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                  <FaYoutube className="text-[#134E4A] hover:text-red-600 transition-colors" size={15} />
                </a>
                <a href="https://t.me/DimensionIAS" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                  <FaTelegramPlane className="text-[#134E4A] hover:text-blue-500 transition-colors" size={15} />
                </a>
                <a href="https://wa.me/917530807530" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                  <FaWhatsapp className="text-[#134E4A] hover:text-green-500 transition-colors" size={15} />
                </a>
                <a
                  href="mailto:contact@dimensionias.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:scale-110 transition-transform"
                >
                  <HiOutlineMail className="text-[#134E4A] hover:text-teal-500 cursor-pointer transition-colors" size={16} />
                </a>
              </div>

              <Link
                to="/contact"
                className="bg-[#073B34] text-white text-xs font-bold px-4 py-2 rounded-xl shadow-md hover:bg-[#0A4D44] transition-all transform hover:scale-105"
              >
                Enquire Now
              </Link>
            </div>
          </div>

          {/* Mobile Toggle Button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-[#134E4A] hover:text-[#2DD4BF] p-2 transition-colors"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white/95 border-t border-[#E0F2F1] absolute top-full left-0 right-0 z-[9999] max-h-[80vh] overflow-y-auto shadow-lg">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                <div key={item.id}>
                  {item.dropdown ? (
                    <div>
                      <button
                        onClick={() => handleMobileDropdownToggle(item.id)}
                        className={`flex items-center justify-between w-full px-3 py-3 text-base font-medium rounded-md transition-colors ${location.pathname.startsWith(item.id)
                          ? 'text-[#134E4A] bg-[#F0FDF7]'
                          : 'text-[#134E4A] hover:text-[#2DD4BF] hover:bg-[#F0FDF7]'
                          }`}
                      >
                        <span>{item.label}</span>
                        <ChevronDown
                          size={16}
                          className={`transition-transform duration-200 ${mobileDropdowns[item.id] ? 'rotate-180' : ''
                            }`}
                        />
                      </button>
                      {mobileDropdowns[item.id] && (
                        <div className="bg-[#F0FDF7] ml-4 rounded-md mt-1 max-h-64 overflow-y-auto border border-[#E0F2F1]">
                          {item.dropdown.map((dropdownItem, index) => (
                            <div key={index}>
                              {dropdownItem.action ? (
                                <button
                                  onClick={dropdownItem.action}
                                  className="block w-full text-left px-4 py-2 text-sm text-[#134E4A] hover:text-[#2DD4BF] hover:bg-white transition-colors border-l-2 border-[#2DD4BF] rounded-l"
                                >
                                  {dropdownItem.label}
                                </button>
                              ) : (
                                <Link
                                  to={dropdownItem.path}
                                  onClick={() => {
                                    setIsMobileMenuOpen(false);
                                    setMobileDropdowns({});
                                  }}
                                  className="block px-4 py-2 text-sm text-[#134E4A] hover:text-[#2DD4BF] hover:bg-white transition-colors border-l-2 border-[#2DD4BF] rounded-l"
                                >
                                  {dropdownItem.label}
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.id}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block px-3 py-3 text-base font-medium rounded-md transition-colors ${location.pathname === item.id
                        ? 'text-[#134E4A] bg-[#F0FDF7]'
                        : 'text-[#134E4A] hover:text-[#2DD4BF] hover:bg-[#F0FDF7]'
                        }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}

              {/* Free Mentorship Button - Mobile */}
              <Link
                to="/counselling"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-center px-4 py-3 mt-4 mx-4 rounded-full text-white bg-[#2DD4BF] hover:bg-[#22b49b] transition-all duration-300 font-semibold shadow-md"
              >
                Free Mentorship
              </Link>
            </div>

            {/* Mobile Social Icons */}
            <div className="flex justify-center space-x-6 px-4 pb-4 pt-3 border-t border-[#E0F2F1] bg-[#F0FDF7]">
              <a href="https://www.instagram.com/dimensionias_pune" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <FaInstagram className="text-[#134E4A] hover:text-pink-400 transition-colors" size={20} />
              </a>
              <a href="https://www.youtube.com/@DimensionIAS" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <FaYoutube className="text-[#134E4A] hover:text-red-600 transition-colors" size={20} />
              </a>
              <a href="https://t.me/DimensionIAS" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <FaTelegramPlane className="text-[#134E4A] hover:text-blue-400 transition-colors" size={20} />
              </a>
              <a href="https://wa.me/917530807530" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <FaWhatsapp className="text-[#134E4A] hover:text-green-400 transition-colors" size={20} />
              </a>
              <a href="mailto:contact@dimensionias.com" target="_blank" rel="noopener noreferrer" className="hover:scale-110 transition-transform">
                <HiOutlineMail className="text-[#134E4A] hover:text-red-400 transition-colors" size={22} />
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;